import { Suspense, lazy } from "react";
import { BarChart3 } from "lucide-react";
import type { ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell } from "./ArtifactShell";

// react-plotly.js pulls in the full plotly.js bundle (~3MB). Lazy-load
// so it never touches initial chunks and only ships when a figure
// actually renders.
const Plot = lazy(() => import("react-plotly.js"));

interface PlotlyFigureJson {
  data?: unknown[];
  layout?: Record<string, unknown>;
  frames?: unknown[];
  config?: Record<string, unknown>;
}

// Returns the Plotly JSON payload from preview.content if it looks valid;
// the preview pipeline emits `{data, layout, ...}` for FIGURE artifacts.
function asPlotlyFigure(content: unknown): PlotlyFigureJson | null {
  if (!content || typeof content !== "object" || Array.isArray(content)) return null;
  const c = content as PlotlyFigureJson;
  if (!Array.isArray(c.data)) return null;
  return c;
}

const Fallback = () => (
  <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
    Loading chart…
  </div>
);

// Inline plot at a compact height; expand-to-modal renders the same
// figure at full viewport so the user can interact with hover/zoom at
// a useful size.
const FigureBody = ({ figure, height }: { figure: PlotlyFigureJson; height: number }) => {
  const layout: Record<string, unknown> = {
    autosize: true,
    margin: { l: 40, r: 16, t: 24, b: 40 },
    ...(figure.layout ?? {}),
  };
  return (
    <Suspense fallback={<Fallback />}>
      <Plot
        data={(figure.data ?? []) as Plotly.Data[]}
        layout={layout as Partial<Plotly.Layout>}
        config={{ displaylogo: false, responsive: true, ...(figure.config ?? {}) }}
        useResizeHandler
        style={{ width: "100%", height }}
      />
    </Suspense>
  );
};

export const FigureArtifact = ({ preview }: { preview: ArtifactPreview }) => {
  const figure = asPlotlyFigure(preview.content);

  if (!figure) {
    // Content didn't shape like a Plotly figure — surface what we got
    // rather than silently rendering blank space.
    return (
      <ArtifactShell preview={preview}>
        <div className="flex items-center gap-3 py-4 text-sm text-muted-foreground">
          <BarChart3 className="size-5" aria-hidden />
          <span>Figure preview is not a Plotly payload.</span>
        </div>
      </ArtifactShell>
    );
  }

  return (
    <ArtifactShell preview={preview} expandedContent={<FigureBody figure={figure} height={720} />}>
      <FigureBody figure={figure} height={320} />
    </ArtifactShell>
  );
};
